import React, { useEffect, useRef, useState } from "react";
import { BookOpen, Upload, FileText, Trash2, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { streamSSE, API_BASE } from "@/lib/api";

const DEPARTMENTS = ["All", "Company", "HR", "Sales", "Finance", "Legal", "Engineering", "Product", "Marketing"];

export default function Knowledge() {
    const [tab, setTab] = useState("docs"); // docs | ask
    const [docs, setDocs] = useState([]);
    const [dept, setDept] = useState("All");
    const [showUpload, setShowUpload] = useState(false);
    const [uploadTab, setUploadTab] = useState("paste");
    const [pasteTitle, setPasteTitle] = useState("");
    const [pasteContent, setPasteContent] = useState("");
    const [pasteDept, setPasteDept] = useState("Company");
    const [fileDept, setFileDept] = useState("Company");
    const [question, setQuestion] = useState("");
    const [answer, setAnswer] = useState("");
    const [citations, setCitations] = useState([]);
    const [asking, setAsking] = useState(false);
    const fileInput = useRef(null);

    const load = async () => {
        const { data } = await api.get("/knowledge/documents", { params: dept === "All" ? {} : { department: dept } });
        setDocs(data.documents || []);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [dept]);

    const submitPaste = async () => {
        if (!pasteTitle.trim() || !pasteContent.trim()) return toast.error("Add title and content");
        try {
            await api.post("/knowledge/paste", { title: pasteTitle, content: pasteContent, department: pasteDept });
            toast.success("Indexed and searchable");
            setShowUpload(false); setPasteTitle(""); setPasteContent(""); setPasteDept("Company");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const submitUpload = async (file) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        fd.append("department", fileDept);
        fd.append("tags", "");
        const token = localStorage.getItem("wfos_token");
        try {
            const res = await fetch(`${API_BASE}/knowledge/upload`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: fd,
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err?.detail || `HTTP ${res.status}`);
            }
            const data = await res.json();
            toast.success(`Indexed ${data.chunks_indexed} chunks`);
            setShowUpload(false);
            await load();
        } catch (e) {
            toast.error(String(e.message || e));
        }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this document?")) return;
        await api.delete(`/knowledge/documents/${id}`);
        await load();
    };

    const ask = async () => {
        if (!question.trim()) return;
        setAsking(true); setAnswer(""); setCitations([]);
        try {
            await streamSSE("/knowledge/ask", { question, department_filter: dept === "All" ? null : dept }, {
                onStart: (p) => setCitations(p.citations || []),
                onDelta: (_, all) => setAnswer(all),
                onDone: (full, p) => { setAnswer(full); if (p.citations) setCitations(p.citations); },
            });
        } catch (e) {
            toast.error(String(e.message || e));
        } finally {
            setAsking(false);
        }
    };

    return (
        <div className="h-full flex bg-white">
            {/* Left rail */}
            <aside className="w-[220px] flex-shrink-0 border-r border-[#e8e8e3] bg-[#fafaf7] p-4 overflow-y-auto wfos-scrollbar">
                <div className="wfos-label mb-2">Departments</div>
                <ul className="space-y-0.5">
                    {DEPARTMENTS.map((d) => (
                        <li key={d}>
                            <button
                                onClick={() => setDept(d)}
                                data-testid={`kn-dept-${d}`}
                                className={`w-full text-left rounded-md px-2 h-8 text-sm hover:bg-white ${dept === d ? "bg-white border border-[#e8e8e3]" : ""}`}
                            >
                                {d}
                            </button>
                        </li>
                    ))}
                </ul>
                <button
                    onClick={() => setShowUpload(true)}
                    data-testid="kn-upload-btn"
                    className="w-full mt-6 inline-flex items-center justify-center gap-1 rounded-lg bg-[#16a34a] px-3 h-9 text-xs font-medium text-white hover:bg-[#15803d]"
                >
                    <Upload className="w-3.5 h-3.5" strokeWidth={2} /> Upload
                </button>
            </aside>

            {/* Main */}
            <main className="flex-1 flex flex-col min-w-0 overflow-y-auto wfos-scrollbar">
                <div className="border-b border-[#e8e8e3] px-6 py-4 flex items-center justify-between gap-3">
                    <div>
                        <div className="wfos-label">Knowledge</div>
                        <div className="mt-1 text-lg font-medium text-gray-900">Ask your company anything.</div>
                    </div>
                    <div className="flex gap-1 rounded-lg border border-[#e8e8e3] bg-white p-1">
                        <button onClick={() => setTab("docs")} data-testid="kn-tab-docs" className={`px-3 h-7 rounded-md text-xs font-medium ${tab === "docs" ? "bg-[#111827] text-white" : "text-gray-600"}`}>Documents</button>
                        <button onClick={() => setTab("ask")} data-testid="kn-tab-ask" className={`px-3 h-7 rounded-md text-xs font-medium ${tab === "ask" ? "bg-[#111827] text-white" : "text-gray-600"}`}>Ask</button>
                    </div>
                </div>

                {tab === "docs" ? (
                    <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {docs.map((d) => (
                            <div key={d.id} data-testid={`kn-doc-${d.id}`} className="wfos-card p-4 flex flex-col">
                                <div className="flex items-start gap-2">
                                    <FileText className="w-4 h-4 text-[#16a34a] flex-shrink-0 mt-0.5" strokeWidth={1.75} />
                                    <div className="min-w-0 flex-1">
                                        <div className="text-[13px] font-semibold text-gray-900 truncate">{d.title}</div>
                                        <div className="mt-0.5 text-[11px] text-gray-500">{d.department} · {d.content_type?.toUpperCase()}</div>
                                    </div>
                                </div>
                                <div className="mt-3 flex flex-wrap gap-1">
                                    {(d.tags || []).slice(0, 3).map((t) => <span key={t} className="wfos-pill text-[9px] px-1.5 py-0.5">{t}</span>)}
                                </div>
                                <div className="mt-3 pt-3 border-t border-[#e8e8e3] flex items-center justify-between text-[11px] text-gray-500">
                                    <span>{new Date(d.uploaded_at).toLocaleDateString()}</span>
                                    <button onClick={() => del(d.id)} className="text-gray-400 hover:text-red-600" title="Delete">
                                        <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                                    </button>
                                </div>
                            </div>
                        ))}
                        {docs.length === 0 && (
                            <div className="col-span-full text-center text-sm text-gray-400 py-16">
                                <BookOpen className="w-8 h-8 mx-auto text-gray-300" strokeWidth={1.5} />
                                <div className="mt-2">No documents in this department yet.</div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="p-6">
                        <div className="max-w-3xl">
                            <div className="wfos-card p-3 flex items-center gap-2">
                                <input
                                    value={question}
                                    onChange={(e) => setQuestion(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && ask()}
                                    data-testid="kn-question"
                                    placeholder="Ask your company anything…"
                                    className="flex-1 h-9 bg-transparent outline-none text-sm"
                                />
                                <button
                                    onClick={ask}
                                    disabled={asking || !question.trim()}
                                    data-testid="kn-ask-submit"
                                    className="inline-flex items-center gap-1 rounded-md bg-[#16a34a] px-3 h-8 text-xs font-medium text-white hover:bg-[#15803d] disabled:opacity-50"
                                >
                                    <Send className="w-3.5 h-3.5" strokeWidth={2} /> {asking ? "…" : "Ask"}
                                </button>
                            </div>

                            {(answer || asking) && (
                                <div className="mt-4 wfos-card p-5" data-testid="kn-answer">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Sparkles className="w-3.5 h-3.5 text-[#16a34a]" strokeWidth={2} />
                                        <div className="wfos-label">Answer</div>
                                    </div>
                                    <div className={`text-sm text-gray-800 leading-relaxed whitespace-pre-wrap ${asking && !answer ? "wfos-caret" : ""}`}>
                                        {answer}
                                    </div>
                                </div>
                            )}

                            {citations.length > 0 && (
                                <div className="mt-4">
                                    <div className="wfos-label mb-2">Sources</div>
                                    <div className="space-y-2">
                                        {citations.map((c) => (
                                            <div key={c.index} className="wfos-card p-3" data-testid={`kn-citation-${c.index}`}>
                                                <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                                                    <span className="wfos-pill text-[9px]">[{c.index}]</span>
                                                    <span className="font-medium text-gray-900">{c.title}</span>
                                                    {c.department && <span>· {c.department}</span>}
                                                </div>
                                                <div className="text-xs text-gray-600 line-clamp-3">{c.excerpt}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </main>

            {showUpload && (
                <div className="fixed inset-0 bg-black/30 grid place-items-center z-50">
                    <div className="wfos-card p-6 w-[560px]" data-testid="kn-upload-modal">
                        <div className="text-lg font-medium text-gray-900">Add to knowledge</div>
                        <div className="mt-3 flex gap-1 border-b border-[#e8e8e3]">
                            <button onClick={() => setUploadTab("paste")} data-testid="kn-upload-paste-tab" className={`px-3 h-8 text-xs font-medium border-b-2 ${uploadTab === "paste" ? "border-[#16a34a] text-gray-900" : "border-transparent text-gray-500"}`}>Paste text</button>
                            <button onClick={() => setUploadTab("file")} data-testid="kn-upload-file-tab" className={`px-3 h-8 text-xs font-medium border-b-2 ${uploadTab === "file" ? "border-[#16a34a] text-gray-900" : "border-transparent text-gray-500"}`}>Upload file</button>
                        </div>
                        {uploadTab === "paste" ? (
                            <div className="mt-4 space-y-3">
                                <input value={pasteTitle} onChange={(e) => setPasteTitle(e.target.value)} placeholder="Title" className="w-full h-9 rounded-md border border-[#e8e8e3] px-2 text-sm" data-testid="kn-paste-title" />
                                <select value={pasteDept} onChange={(e) => setPasteDept(e.target.value)} className="w-full h-9 rounded-md border border-[#e8e8e3] px-2 text-sm">
                                    {DEPARTMENTS.filter((d) => d !== "All").map((d) => <option key={d} value={d}>{d}</option>)}
                                </select>
                                <textarea value={pasteContent} onChange={(e) => setPasteContent(e.target.value)} placeholder="Paste your content…" className="w-full h-40 rounded-md border border-[#e8e8e3] px-2 py-1.5 text-sm resize-none" data-testid="kn-paste-content" />
                                <div className="flex justify-end gap-2 pt-1">
                                    <button onClick={() => setShowUpload(false)} className="rounded-md border border-[#e8e8e3] px-3 h-8 text-xs text-gray-700">Cancel</button>
                                    <button onClick={submitPaste} data-testid="kn-paste-submit" className="rounded-md bg-[#16a34a] px-3 h-8 text-xs font-medium text-white">Save & index</button>
                                </div>
                            </div>
                        ) : (
                            <div className="mt-4 space-y-3">
                                <select value={fileDept} onChange={(e) => setFileDept(e.target.value)} className="w-full h-9 rounded-md border border-[#e8e8e3] px-2 text-sm">
                                    {DEPARTMENTS.filter((d) => d !== "All").map((d) => <option key={d} value={d}>{d}</option>)}
                                </select>
                                <label
                                    htmlFor="kn-file"
                                    className="block rounded-lg border-2 border-dashed border-[#e8e8e3] p-8 text-center text-sm text-gray-500 hover:border-[#d6d6cf] cursor-pointer"
                                >
                                    <Upload className="w-5 h-5 mx-auto text-gray-400" strokeWidth={1.5} />
                                    <div className="mt-2">Click to upload · PDF, DOCX, TXT, MD</div>
                                </label>
                                <input
                                    id="kn-file"
                                    ref={fileInput}
                                    type="file"
                                    accept=".pdf,.docx,.txt,.md"
                                    onChange={(e) => submitUpload(e.target.files?.[0])}
                                    data-testid="kn-file-input"
                                    className="hidden"
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
